"use client";

import { Trans } from "@lingui/react/macro";
import {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import {
  ToolDisclosureButton,
  ToolInput,
  ToolOutput,
} from "@/components/ai-elements/tool";
import { getMessagePartRenderSignature } from "./part-signature";
import type {
  ActivityToolDisclosure,
  ActivityToolPresentation,
  ToolRenderPlan,
} from "./render-model";
import { getRenderableToolOutput } from "./tool-output-presentation";

const APPROVAL_RESULT_LABEL_KEYS = [
  "viewName",
  "projectName",
  "dashboardName",
  "groupName",
  "propertyName",
  "serviceAccountName",
  "name",
  "title",
] as const;

/** 承認済みtoolの結果から、IDを含まない人向けのresource名だけを選びます。 */
function getApprovalResultLabel(output: unknown): string | null {
  if (!output || typeof output !== "object" || Array.isArray(output)) {
    return null;
  }
  for (const key of APPROVAL_RESULT_LABEL_KEYS) {
    const value = Reflect.get(output, key);
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

/** output-available でも業務結果が失敗なら成功表示へ丸めない。 */
function hasApplicationLevelFailure(output: unknown): boolean {
  if (!output || typeof output !== "object" || Array.isArray(output)) {
    return false;
  }
  if (Reflect.get(output, "error") != null) return true;
  if (Reflect.get(output, "ok") === false) return true;
  const failureCount = Reflect.get(output, "failureCount");
  if (typeof failureCount === "number" && failureCount > 0) return true;
  const runError = Reflect.get(output, "runError");
  if (typeof runError === "string" && runError.trim()) return true;

  const summary = Reflect.get(output, "summary");
  return (
    Boolean(summary) &&
    typeof summary === "object" &&
    !Array.isArray(summary) &&
    typeof Reflect.get(summary, "failureCount") === "number" &&
    Reflect.get(summary, "failureCount") > 0
  );
}

function ToolActivityDetails({
  plan,
  presentation,
}: {
  plan: ToolRenderPlan;
  presentation: ActivityToolPresentation;
}): ReactElement {
  const output = useMemo(
    () => getRenderableToolOutput(plan.part.output),
    [plan.part.output],
  );
  const approvalResultLabel = useMemo(
    () => getApprovalResultLabel(output),
    [output],
  );
  const applicationFailed = useMemo(
    () => hasApplicationLevelFailure(output),
    [output],
  );
  const density = presentation === "live" ? "default" : "compact";
  const shouldShowOutput =
    plan.placement !== "activity-and-standalone" ||
    plan.part.state === "output-error" ||
    plan.part.state === "output-denied";

  // approval review は「何を承認したか」を残す監査情報なので、専用結果 UI の有無や
  // parser fallback 後の renderer に左右されず、terminal Activity の詳細へ表示する。
  if (plan.renderer === "confirmation") {
    return (
      <div className="space-y-2 border-border/60 border-t px-1 py-2">
        {plan.approvalReview?.node ? (
          <div>{plan.approvalReview.node}</div>
        ) : null}
        {plan.node ? (
          <div>{plan.node}</div>
        ) : plan.part.state === "output-error" ? (
          <ToolOutput
            density={density}
            errorText={plan.part.errorText}
            output={output}
          />
        ) : plan.part.state === "output-denied" ? (
          <div className="space-y-1 text-muted-foreground text-xs">
            <p>
              {plan.part.approval?.isAutomatic === true ? (
                <Trans>This tool execution was denied automatically.</Trans>
              ) : (
                <Trans>You denied this tool execution.</Trans>
              )}
            </p>
            {plan.part.approval?.reason ? (
              <p>{plan.part.approval.reason}</p>
            ) : null}
          </div>
        ) : applicationFailed ? (
          <ToolOutput
            density={density}
            errorText={plan.part.errorText}
            output={output}
          />
        ) : (
          <p className="text-muted-foreground text-xs">
            {approvalResultLabel ?? <Trans>Completed</Trans>}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="border-border/60 border-t">
      {plan.approvalReview?.node ? (
        <div className="px-1 py-2">{plan.approvalReview.node}</div>
      ) : null}
      {plan.part.input ? (
        <ToolInput density={density} input={plan.part.input} />
      ) : null}
      {shouldShowOutput ? (
        <ToolOutput
          className="border-border/50 border-t first:border-t-0"
          density={density}
          errorText={plan.part.errorText}
          output={output}
        />
      ) : null}
    </div>
  );
}

type ToolActivityRowProps = {
  disclosure?: ActivityToolDisclosure;
  plan: ToolRenderPlan;
  isStreaming: boolean;
  presentation?: ActivityToolPresentation;
};

function ToolActivityRowComponent({
  disclosure,
  plan,
  isStreaming,
  presentation = "history",
}: ToolActivityRowProps): ReactElement {
  const isLive = presentation === "live";
  const isFailed =
    plan.part.state === "output-error" ||
    (plan.part.state === "output-available" &&
      hasApplicationLevelFailure(plan.part.output));
  // approval-responded は承認・拒否の両方に使われるため、拒否済みの表示を
  // 汎用の「承認応答済み」へ丸めず terminal denial と同じ見た目に揃える。
  const displayState =
    plan.part.state === "approval-responded" &&
    plan.part.approval?.approved === false
      ? "output-denied"
      : isFailed && plan.part.state === "output-available"
        ? "output-error"
        : plan.part.state;
  const contentId = useId();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(isFailed);
  const open = disclosure?.open ?? uncontrolledOpen;
  const [deferredDetailsReady, setDeferredDetailsReady] = useState(false);
  const onOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (disclosure) {
        disclosure.onOpenChange(nextOpen);
      } else {
        setUncontrolledOpen(nextOpen);
      }
      if (!nextOpen) setDeferredDetailsReady(false);
    },
    [disclosure],
  );
  const shouldDeferDetails = open && isStreaming;
  const shouldMountDetails =
    open && (!shouldDeferDetails || deferredDetailsReady);

  useEffect(() => {
    if (!shouldDeferDetails || deferredDetailsReady) return;

    // streaming の描画更新と重い JSON highlight を同じ frame で処理しない。
    if (typeof window.requestIdleCallback === "function") {
      const idleCallbackId = window.requestIdleCallback(
        () => setDeferredDetailsReady(true),
        { timeout: 150 },
      );
      return () => window.cancelIdleCallback(idleCallbackId);
    }

    const timeoutId = window.setTimeout(
      () => setDeferredDetailsReady(true),
      50,
    );
    return () => window.clearTimeout(timeoutId);
  }, [deferredDetailsReady, shouldDeferDetails]);

  const isRunning = [
    "input-streaming",
    "input-available",
    "approval-requested",
    "approval-responded",
  ].includes(plan.part.state);

  useEffect(() => {
    if (isFailed && !disclosure) {
      setUncontrolledOpen(true);
    }
  }, [disclosure, isFailed]);

  return (
    <div
      className="mb-0 w-full max-w-full overflow-hidden border-0 bg-transparent"
      data-ai-tool=""
    >
      <ToolDisclosureButton
        className={
          isLive
            ? "h-8 rounded-sm px-0 py-0 transition-colors hover:bg-muted/35"
            : "-mx-1 min-h-6 w-fit max-w-full rounded-sm px-1 py-0 transition-colors hover:bg-muted/45"
        }
        controlsId={contentId}
        layout={isLive ? "card" : "activity"}
        onBlur={disclosure?.onTriggerBlur}
        onFocus={disclosure?.onTriggerFocus}
        onOpenChange={onOpenChange}
        open={open}
        shimmerTitle={isLive && isRunning}
        showIcon={false}
        state={displayState}
        titleTone="muted"
        type={plan.part.type}
        title={plan.title}
      />
      {open ? (
        <div data-slot="collapsible-content" id={contentId}>
          {shouldMountDetails ? (
            <ToolActivityDetails plan={plan} presentation={presentation} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function areToolActivityRowPropsEqual(
  previous: ToolActivityRowProps,
  next: ToolActivityRowProps,
): boolean {
  const previousPart = previous.plan.part;
  const nextPart = next.plan.part;

  return (
    previous.isStreaming === next.isStreaming &&
    previous.presentation === next.presentation &&
    previous.disclosure?.open === next.disclosure?.open &&
    previous.plan.id === next.plan.id &&
    previous.plan.placement === next.plan.placement &&
    previous.plan.title === next.plan.title &&
    (previousPart === nextPart ||
      getMessagePartRenderSignature(previousPart) ===
        getMessagePartRenderSignature(nextPart))
  );
}

/**
 * Activity 内では tool 名だけを見出しにし、入出力は展開後の詳細へ送る。
 * streaming の text 更新だけでは、既に開いた Tool subtree を再描画しない。
 */
export const ToolActivityRow = memo(
  ToolActivityRowComponent,
  areToolActivityRowPropsEqual,
);
